import countBy from 'lodash/fp/countBy';
import groupBy from 'lodash/fp/groupBy';
import keyBy from 'lodash/fp/keyBy';
import omit from 'lodash/fp/omit';

import type {
    Asset,
    CombinedAssetData,
    Driver,
    DrivingTimes,
    Geofence,
    Group,
    LiveState,
    PlatformNotification,
    PlatformNotificationsCount,
} from '../types';

export type CombinedApiData = {
    liveStates: LiveState[];
    drivingTimes: DrivingTimes[];
    assets: Asset[];
    drivers: Driver[];
    platformNotifications: PlatformNotification[];
    geofences: Geofence[];
    groups: Group[];
};

const removeGroupsFromDrivers = (drivers: Driver[]) =>
    drivers.map(driver => ({
        driverId: driver.driverId,
        driverName: driver.driverName,
        firstName: driver.firstName,
        lastName: driver.lastName,
    }));

export const countPlatformNotifications = (
    platformNotifications: PlatformNotification[]
): PlatformNotificationsCount => {
    const { HIGH: exceptions, MEDIUM: warnings } = countBy('importance', platformNotifications);
    return {
        notificationExceptions: exceptions || 0,
        notificationWarnings: warnings || 0,
    };
};

const calculateGroupNames = (groupIds: string[] | undefined, groups: Group[]): string[] => {
    if (!groupIds || groupIds.length === 0) {
        return [];
    }
    const groupMap = new Map(groups.map(group => [group.id, group.name]));
    return groupIds.map(id => groupMap.get(id)).filter((name): name is string => name !== undefined);
};

export const mapAssetsAndDriversWithStates = (apiData: CombinedApiData): CombinedAssetData[] => {
    const { assets, liveStates, drivers, drivingTimes, platformNotifications, geofences } = apiData;
    const assetStatesDictionary = keyBy('vehicleId', liveStates);
    const driversDictionary = keyBy('driverId', removeGroupsFromDrivers(drivers));
    const drivingTimesDictionary = keyBy('driverId', drivingTimes);
    const notificationsDictionary = groupBy('assetId', platformNotifications);
    const geofencesDictionary = keyBy('id', geofences);

    return assets.map((asset: Asset) => {
        const vehicleId = asset.vehicleId;

        const liveState: LiveState | undefined = assetStatesDictionary[vehicleId];

        const driverData: Driver | undefined = liveState?.driverId ? driversDictionary[liveState.driverId] : undefined;

        const drivingTimesData: DrivingTimes | undefined =
            liveState?.driverId &&
            drivingTimesDictionary[liveState.driverId] &&
            drivingTimesDictionary[liveState.driverId].vehicleId === vehicleId
                ? drivingTimesDictionary[liveState.driverId]
                : undefined;

        const notificationsCount = countPlatformNotifications(notificationsDictionary[asset.vehicleId] || []);

        const insideGeofences = liveState?.insideGeofences?.map(insideGeofence => {
            const name = geofencesDictionary[insideGeofence.geofenceId]?.name;
            return { ...insideGeofence, name };
        });

        const updatedLiveState = { ...liveState, insideGeofences };
        const groupNames = calculateGroupNames(asset.groupIds, apiData.groups);

        const updatedAsset = { ...asset, groupNames };

        return {
            ...updatedAsset,
            ...updatedLiveState,
            ...omit('groupIds', driverData),
            ...drivingTimesData,
            ...notificationsCount,
        } as CombinedAssetData;
    });
};
